"use client";

import { useIsShopsite } from "@context/DomainContext";
import { LocaleSwitcher } from "@features/localeSwitcher";
import {
  ArrowLeftEndOnRectangleIcon,
  ArrowRightStartOnRectangleIcon,
  ClockIcon,
  IdentificationIcon,
  InboxStackIcon,
  TicketIcon,
  UserIcon,
  ArrowTopRightOnSquareIcon,
} from "@heroicons/react/24/outline";
import * as Dialog from "@radix-ui/react-dialog";
import { Heading, Text } from "@components/theming";
import { createBrowserClient } from "@supabase/ssr";
import { WhitelabelConfig } from "@types";
import {
  COUNTRY_CODE,
  SUPABASE_ANON_KEY,
  SUPABASE_URL,
} from "@utils/constants";
import { authorizedFetcher } from "@utils/fetchers";
import { joinClassNames } from "@utils/helpers";
import { useSupabaseSession } from "@utils/hooks";
import { AnimatePresence, motion } from "framer-motion";
import { useTranslations } from "next-intl";
import Link, { LinkProps } from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import useSWR from "swr";
import { auGenreIds, genreIds } from "@features/genreEvents/utils/genreIds";
import SearchResults from "@components/searchResults";
import WavingHand from "@components/wavingHand";

interface NavLinkProps {
  href: string;
  basePath?: string;
  label: string;
  icon?: React.ReactElement;
  target?: string;
  setOpen: (open: boolean) => void;
  iconPos?: "start" | "end";
}

const NavLink = ({
  href,
  basePath,
  label,
  icon,
  setOpen,
  target,
  iconPos = "start",
  ...linkProps
}: NavLinkProps & LinkProps) => {
  const pathname = usePathname();
  const isPath = basePath ? pathname.startsWith(basePath) : pathname === href;

  return (
    <Text
      asChild
      size="4"
      weight="medium"
      color={!isPath ? "gray" : undefined}
      sharedClasses={joinClassNames(
        "flex items-center px-3 py-2",
        iconPos === "start" ? "gap-3" : "gap-2"
      )}
      shopsiteClasses={joinClassNames(
        "text-lg font-medium rounded-xl",
        isPath ? "text-gray-300 bg-white/10" : "text-gray-400"
      )}
      whitelabelClasses={joinClassNames(
        "rounded-[var(--radius-3)]",
        isPath ? "bg-[color:var(--gray-a4)]" : ""
      )}
    >
      <Link
        href={href}
        onClick={() => setOpen(false)}
        target={target}
        {...linkProps}
      >
        {iconPos === "start" && icon}
        {label}
        {iconPos === "end" && icon}
      </Link>
    </Text>
  );
};

interface Props {
  showSearch: boolean;
  language?: WhitelabelConfig["website"]["language"];
  colour?: WhitelabelConfig["settings"]["header"]["colour"];
  textColour?: WhitelabelConfig["settings"]["header"]["textColour"];
  show_banner?: boolean;
}

export default function MobileMenu({
  showSearch,
  language,
  colour,
  textColour,
  show_banner,
}: Props) {
  const router = useRouter();
  const supabase = createBrowserClient(SUPABASE_URL!, SUPABASE_ANON_KEY!);
  const session = useSupabaseSession();
  const accountTranslations = useTranslations("Account.popover");
  const navbarTranslations = useTranslations("Navbar");
  const genreTranslations = useTranslations("GenrePage.genres");
  const [open, setOpen] = useState(false);
  const isShopsite = useIsShopsite();

  const genres = COUNTRY_CODE === "au" ? auGenreIds : genreIds;

  const { data: account } = useSWR(
    session && session.access_token ? ["account", session?.access_token] : null,
    authorizedFetcher,
    {
      revalidateOnFocus: false,
      revalidateIfStale: false,
      dedupingInterval: 10000,
    }
  );

  useEffect(() => {
    if (open) {
      document.body.className = "overflow-y-hidden";
    } else {
      document.body.className = "";
    }
  }, [open]);

  return (
    <div className="relative h-6">
      <Dialog.Root modal={false} open={open} onOpenChange={setOpen}>
        <Dialog.Trigger
          className={joinClassNames(
            "absolute top-0 right-0 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-indigo-500 transition-colors ease-in-out duration-200",
            isShopsite ? "text-gray-600" : "text-white"
          )}
        >
          <span className="sr-only">{navbarTranslations("openmainmenu")}</span>
          <motion.svg
            xmlns="http://www.w3.org/2000/svg"
            className="w-6 h-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke={isShopsite ? "#ffffff" : textColour}
            strokeWidth="2"
            aria-hidden="true"
          >
            <motion.line
              x1={4}
              x2={20}
              y1={6}
              y2={6}
              style={{
                originX: "12px",
                originY: "6px",
              }}
              animate={
                open
                  ? {
                      rotate: 45,
                      translateY: 6,
                    }
                  : {
                      rotate: 0,
                      translateY: 0,
                    }
              }
              strokeLinecap="round"
              strokeLinejoin="round"
              transition={{ type: "tween", duration: 0.2 }}
            />
            <motion.line
              x1={4}
              x2={20}
              y1={12}
              y2={12}
              initial={{ opacity: 1 }}
              animate={open ? { opacity: 0 } : { opacity: 1 }}
              strokeLinecap="round"
              strokeLinejoin="round"
              transition={{ type: "tween", duration: 0.15 }}
            />
            <motion.line
              x1={4}
              x2={20}
              y1={18}
              y2={18}
              style={{
                originX: "12px",
                originY: "18px",
              }}
              animate={
                open
                  ? {
                      rotate: -45,
                      translateY: -6,
                    }
                  : {
                      rotate: 0,
                      translateY: 0,
                    }
              }
              strokeLinecap="round"
              strokeLinejoin="round"
              transition={{ type: "tween", duration: 0.2 }}
            />
          </motion.svg>
        </Dialog.Trigger>
        <AnimatePresence>
          {open && (
            <Dialog.Portal
              forceMount
              container={document.getElementById(
                isShopsite ? "shopsite-root-element" : "whitelabel-root-element"
              )}
            >
              <Dialog.Content
                asChild
                forceMount
                className={joinClassNames(
                  "fixed inset-0 z-[49] w-screen",
                  isShopsite ? "" : "mt-[80px]"
                )}
              >
                <motion.div
                  initial="hidden"
                  animate="show"
                  exit="hidden"
                  variants={{
                    hidden: { height: 0, transition: { duration: 0.125 } },
                    show: { height: "auto" },
                  }}
                  transition={{ duration: 0.25 }}
                >
                  <nav
                    style={{ backgroundColor: isShopsite ? "#000000" : colour }}
                    className={joinClassNames(
                      "h-full",
                      isShopsite
                        ? show_banner
                          ? "pt-[138px]"
                          : "pt-[5.5rem]"
                        : ""
                    )}
                    aria-label={accountTranslations("mobilemenutitle")}
                  >
                    <Dialog.Title className="sr-only">
                      {accountTranslations("mobilemenutitle")}
                    </Dialog.Title>
                    <Dialog.Description className="sr-only">
                      {accountTranslations("mobilemenudescription")}
                    </Dialog.Description>
                    <div className="box-border flex flex-col h-full">
                      <div className="overflow-y-auto grow">
                        <div className="px-2 pb-4 mx-auto max-w-8xl">
                          {isShopsite && showSearch && (
                            <div className="mb-2">
                              <SearchResults onClick={() => setOpen(false)} />
                            </div>
                          )}
                          {account ? (
                            <h2 className={"flex items-center gap-1 p-1.5"}>
                              <WavingHand fill="#facc15" />
                              <Text
                                size="7"
                                style={{
                                  color: !isShopsite ? textColour : undefined,
                                }}
                                weight="bold"
                                shopsiteClasses="text-3xl font-black text-white"
                              >
                                {accountTranslations("hi", {
                                  name: account?.firstname,
                                })}
                              </Text>
                            </h2>
                          ) : (
                            <></>
                          )}
                          {session ? (
                            <>
                              <NavLink
                                href="/account"
                                label={accountTranslations("myaccount")}
                                icon={<UserIcon className="h-6 w-6" />}
                                setOpen={setOpen}
                              />
                              <NavLink
                                href="/account/my-tickets"
                                label={accountTranslations("mytickets")}
                                icon={<TicketIcon className="h-6 w-6" />}
                                setOpen={setOpen}
                              />
                              <NavLink
                                href="/account/order-history"
                                label={accountTranslations("orderhistory")}
                                icon={<ClockIcon className="h-6 w-6" />}
                                setOpen={setOpen}
                              />
                              {COUNTRY_CODE !== "au" && (
                                <NavLink
                                  href="/account/e-pass"
                                  label={accountTranslations("epass")}
                                  icon={
                                    <IdentificationIcon className="h-6 w-6" />
                                  }
                                  setOpen={setOpen}
                                />
                              )}
                              {COUNTRY_CODE !== "au" && (
                                <NavLink
                                  href="/account/subscription-preferences/updates"
                                  basePath="/account/subscription-preferences/"
                                  label={accountTranslations(
                                    "subscriptionpreferences"
                                  )}
                                  icon={<InboxStackIcon className="h-6 w-6" />}
                                  setOpen={setOpen}
                                />
                              )}
                              <Text
                                asChild
                                size="4"
                                weight="medium"
                                color="gray"
                                shopsiteClasses="text-lg font-medium text-gray-400"
                              >
                                <button
                                  onClick={async () => {
                                    await supabase.auth.signOut();
                                    setOpen(false);
                                    router.refresh();
                                  }}
                                  className="flex gap-3 items-center px-3 py-2 focus:outline-none"
                                >
                                  <ArrowRightStartOnRectangleIcon className="h-6 w-6" />
                                  {accountTranslations("signout")}
                                </button>
                              </Text>
                            </>
                          ) : (
                            <>
                              <Heading
                                as="h2"
                                size="6"
                                weight="bold"
                                style={{
                                  color: !isShopsite ? textColour : undefined,
                                }}
                                sharedClasses="px-1.5"
                                shopsiteClasses="text-2xl text-white font-extrabold"
                              >
                                {navbarTranslations("account")}
                              </Heading>
                              <NavLink
                                href="/account/sign-in"
                                label={navbarTranslations("signin")}
                                icon={
                                  <ArrowLeftEndOnRectangleIcon className="h-6 w-6" />
                                }
                                setOpen={setOpen}
                              />
                            </>
                          )}
                          {isShopsite && (
                            <>
                              <Heading
                                as="h2"
                                size="6"
                                weight="bold"
                                style={{
                                  color: !isShopsite ? textColour : undefined,
                                }}
                                sharedClasses="mt-2 px-1.5"
                                shopsiteClasses="text-2xl text-white font-extrabold"
                              >
                                {navbarTranslations("genres")}
                              </Heading>
                              {Object.entries(genres)
                                .filter(([_, value]) => value.id !== 0)
                                .map(([key, value]) => (
                                  <NavLink
                                    key={key}
                                    href={`/whats-on/${key}`}
                                    label={genreTranslations(
                                      value.translationKey
                                    )}
                                    setOpen={setOpen}
                                  />
                                ))}
                              <Heading
                                as="h2"
                                size="6"
                                weight="bold"
                                style={{
                                  color: !isShopsite ? textColour : undefined,
                                }}
                                sharedClasses="mt-2 px-1.5"
                                shopsiteClasses="text-2xl text-white font-extrabold"
                              >
                                {navbarTranslations("support")}
                              </Heading>
                              <NavLink
                                href="/contact"
                                label={navbarTranslations("contact")}
                                setOpen={setOpen}
                              />
                              <NavLink
                                href="/frequently-asked-questions"
                                label={navbarTranslations("faqs")}
                                setOpen={setOpen}
                              />
                              <Heading
                                as="h2"
                                size="6"
                                weight="bold"
                                style={{
                                  color: !isShopsite ? textColour : undefined,
                                }}
                                sharedClasses="mt-2 px-1.5"
                                shopsiteClasses="text-2xl text-white font-extrabold"
                              >
                                {navbarTranslations("legal")}
                              </Heading>
                              <NavLink
                                href="/legal/terms"
                                label={navbarTranslations("terms")}
                                setOpen={setOpen}
                                prefetch={false}
                              />
                              <NavLink
                                href="/legal/privacy"
                                label={navbarTranslations("privacy")}
                                setOpen={setOpen}
                                prefetch={false}
                              />
                              {COUNTRY_CODE === "nz" && (
                                <>
                                  <Heading
                                    as="h2"
                                    size="6"
                                    weight="bold"
                                    style={{
                                      color: !isShopsite
                                        ? textColour
                                        : undefined,
                                    }}
                                    sharedClasses="mt-2 px-1.5"
                                    shopsiteClasses="text-2xl text-white font-extrabold"
                                  >
                                    {navbarTranslations("links")}
                                  </Heading>
                                  <NavLink
                                    href="https://blog.iticket.co.nz/"
                                    label={navbarTranslations("blog")}
                                    setOpen={setOpen}
                                    target="_blank"
                                    icon={
                                      <ArrowTopRightOnSquareIcon className="h-5 w-5 stroke-2" />
                                    }
                                    iconPos="end"
                                  />
                                  <NavLink
                                    href="https://boxoffice.iticket.co.nz/"
                                    label={navbarTranslations("boxoffice")}
                                    setOpen={setOpen}
                                    target="_blank"
                                    icon={
                                      <ArrowTopRightOnSquareIcon className="h-5 w-5 stroke-2" />
                                    }
                                    iconPos="end"
                                  />
                                </>
                              )}
                            </>
                          )}
                          {language && (
                            <>
                              <Heading
                                as="h2"
                                size="6"
                                weight="bold"
                                style={{
                                  color: !isShopsite ? textColour : undefined,
                                }}
                                sharedClasses="mt-2 px-1.5"
                                shopsiteClasses="text-2xl text-white font-extrabold"
                              >
                                {navbarTranslations("language")}
                              </Heading>
                              <div className="my-2 px-3">
                                <LocaleSwitcher language={language} />
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </nav>
                </motion.div>
              </Dialog.Content>
            </Dialog.Portal>
          )}
        </AnimatePresence>
      </Dialog.Root>
    </div>
  );
}
